import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useDarkMode } from '../../../context/DarkModeContext'
import type { FullEventData, TicketTier } from './types'

const defaultEventForm: Partial<FullEventData> = {
  eventType: '',
  eventName: '',
  shortDescription: '',
  longDescription: '',
  status: 'upcoming',
  startDate: '',
  startTime: '',
  endDate: '',
  endTime: '',
  timezone: '',
  isVirtual: false,
  location: {
    venueName: '',
    venueType: '',
    streetAddress: '',
    city: '',
    region: '',
    country: '',
    postcode: '',
    roomOrTable: '',
    mapLink: '',
  },
  virtualDetails: {
    platform: '',
    accessLink: '',
    replayAvailable: false,
  },
  accessRules: {
    ticketRequired: false,
    ticketLink: '',
    ticketTiers: [],
    ageRestriction: 'none',
    bookLimitPolicy: '',
    preorderRequired: false,
    outsideBooksAllowed: true,
    photosAllowed: true,
    personalisationAllowed: true,
  },
  visibility: {
    showOnProfile: true,
    showInSearch: true,
    allowCalendar: true,
    allowReminders: true,
  },
}

export function useEventManager() {
  const { darkMode } = useDarkMode()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const initialId = searchParams.get('id')
  const [mode, setMode] = useState<'create' | 'edit'>(
    initialId ? 'edit' : 'create',
  )
  const [formData, setFormData] =
    useState<Partial<FullEventData>>(defaultEventForm)

  const [showConfirmation, setShowConfirmation] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [savedAsDraft, setSavedAsDraft] = useState(false)

  // Auto-detect timezone on mount
  useEffect(() => {
    if (mode === 'create' && !formData.timezone) {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
        setFormData((prev) => ({ ...prev, timezone: tz }))
      } catch (e) {
        setFormData((prev) => ({ ...prev, timezone: 'America/New_York' }))
      }
    }
  }, [mode])

  // Mock loading existing data if in edit mode
  useEffect(() => {
    if (mode === 'edit' && initialId) {
      // In a real app, fetch data here
      // For now, we'll just set a mock title to show edit mode works
      setFormData((prev) => ({
        ...prev,
        eventName: 'Existing Event (Mock)',
        eventType: 'book-signing-local',
      }))
    }
  }, [mode, initialId])

  // Form field update handler
  const updateField = (field: keyof FullEventData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const updateNestedField = (
    parent: 'location' | 'virtualDetails' | 'accessRules' | 'visibility',
    field: string,
    value: any,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [parent]: {
        ...(prev[parent] as any),
        [field]: value,
      },
    }))
  }

  // Ticket Tier Handlers
  const addTicketTier = () => {
    const newTier: TicketTier = {
      id: Math.random().toString(36).substr(2, 9),
      name: '',
      price: 0,
      currency: 'USD',
      description: '',
    }
    setFormData((prev) => ({
      ...prev,
      accessRules: {
        ...prev.accessRules!,
        ticketTiers: [...(prev.accessRules?.ticketTiers || []), newTier],
      },
    }))
  }

  const updateTicketTier = (
    id: string,
    field: keyof TicketTier,
    value: any,
  ) => {
    setFormData((prev) => ({
      ...prev,
      accessRules: {
        ...prev.accessRules!,
        ticketTiers: prev.accessRules!.ticketTiers.map((tier) =>
          tier.id === id ? { ...tier, [field]: value } : tier,
        ),
      },
    }))
  }

  const removeTicketTier = (id: string) => {
    setFormData((prev) => ({
      ...prev,
      accessRules: {
        ...prev.accessRules!,
        ticketTiers: prev.accessRules!.ticketTiers.filter(
          (tier) => tier.id !== id,
        ),
      },
    }))
  }

  // Save/Publish Handlers
  const handlePublish = () => {
    setSavedAsDraft(false)
    setIsProcessing(true)
    setTimeout(() => {
      setIsProcessing(false)
      setShowConfirmation(true)
    }, 1500)
  }

  const handleSaveDraft = () => {
    setSavedAsDraft(true)
    setIsProcessing(true)
    setTimeout(() => {
      setIsProcessing(false)
      setShowConfirmation(true)
    }, 1000)
  }

  return {
    darkMode,
    navigate,
    mode,
    formData,
    updateField,
    updateNestedField,
    addTicketTier,
    updateTicketTier,
    removeTicketTier,
    showConfirmation,
    isProcessing,
    savedAsDraft,
    handlePublish,
    handleSaveDraft,
  }
}

export type EM = ReturnType<typeof useEventManager>
